import { FilterListRounded } from "@mui/icons-material";
import { Badge, Button, IconButton, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { MetricsFilterByOption } from "src/constants";
import { useGetMetricCollection } from "src/http";

interface Props {
  variant: "text" | "icon";
  metricCollectionGuid: string;
  filterByOption: MetricsFilterByOption;
  filterByValue: string | undefined;
  onFilterChanged: (filterByOption: MetricsFilterByOption, filterByValue: string | undefined) => void;
}

const MetricsCollectionFilterButton = (props: Props) => {
  const { data: metrics, isLoading } = useGetMetricCollection(props.metricCollectionGuid);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function handleFilterByOptionChanged(option: MetricsFilterByOption) {
    // Ignore other options that require a value selection from a submenu
    if (option != null && option === MetricsFilterByOption.All) {
      props.onFilterChanged(option, undefined);
    }
  }

  function handleFilterByValueChanged(option: MetricsFilterByOption, value: string | undefined) {
    if (value != null) {
      props.onFilterChanged(option, value);
    }
  }

  const teamOptions = _.uniq(metrics?.map((metric) => metric.assignedTeam?.name ?? "None") ?? []).sort(
    filterOptionSort
  );
  const reportingUserOptions = _.uniq(metrics?.map((metric) => metric.reportingUser?.displayName ?? "None") ?? []).sort(
    filterOptionSort
  );

  function filterOptionSort(a: string, b: string) {
    if (a === "None") {
      return -1;
    }

    if (b === "None") {
      return 1;
    }

    return a.localeCompare(b);
  }

  return (
    <>
      <Badge color="primary" variant="dot" invisible={props.filterByOption === MetricsFilterByOption.All}>
        {props.variant === "text" && (
          <Button
            variant="tertiary"
            disabled={isLoading}
            startIcon={<FilterListRounded />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            Filter
          </Button>
        )}

        {props.variant === "icon" && (
          <IconButton variant="tertiary" disabled={isLoading} onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <FilterListRounded />
          </IconButton>
        )}
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        <ToggleButtonGroup
          value={props.filterByOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, option) => handleFilterByOptionChanged(option)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value={MetricsFilterByOption.All}>
            <Typography>All</Typography>
          </ToggleButton>
          <ToggleButtonSubmenu
            disabled={teamOptions.length <= 1}
            optionName="Team"
            option={MetricsFilterByOption.Team}
            value={props.filterByOption === MetricsFilterByOption.Team ? props.filterByValue : undefined}
            values={teamOptions}
            onFilterChanged={handleFilterByValueChanged}
          />
          <ToggleButtonSubmenu
            disabled={reportingUserOptions.length <= 1}
            optionName="Reporting Person"
            option={MetricsFilterByOption.ReportingPerson}
            value={props.filterByOption === MetricsFilterByOption.ReportingPerson ? props.filterByValue : undefined}
            values={reportingUserOptions}
            onFilterChanged={handleFilterByValueChanged}
          />
          {/* <ToggleButtonSubmenu optionName="Tag" value={MetricsFilterByOption.Tag} /> */}
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

interface ToggleButtonSubmenuProps {
  disabled: boolean;
  optionName: string;
  option: MetricsFilterByOption;
  value: string | undefined;
  values: string[];
  onFilterChanged: (option: MetricsFilterByOption, value: string | undefined) => void;
}

const ToggleButtonSubmenu = (props: ToggleButtonSubmenuProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <ToggleButton value={props.option} disabled={props.disabled} onClick={(e) => setMenuAnchor(e.currentTarget)}>
        <Typography>{`${props.optionName} ${props.value ? `(${props.value})` : ""}`}</Typography>
      </ToggleButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        transformOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <ToggleButtonGroup
          variant="menu"
          orientation="vertical"
          exclusive
          value={props.value}
          onChange={(_, value) => props.onFilterChanged(props.option, value)}
          sx={{ minWidth: "100%" }}
        >
          {props.values.map((value) => (
            <ToggleButton value={value}>
              <Typography>{value}</Typography>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

export { MetricsCollectionFilterButton };
